import { useEffect, useState } from "react";
import { AppShell, type PageName } from "./components/AppShell";
import { LoginScreen } from "./features/auth/LoginScreen";
import { RecoveryScreen } from "./features/auth/RecoveryScreen";
import { SetupScreen } from "./features/auth/SetupScreen";
import { ControllerDashboard } from "./features/session/ControllerDashboard";
import {
  DevicesPage,
  HistoryPage,
  SafetyPage
} from "./features/session/SecondaryPages";
import { WearerDashboard } from "./features/session/WearerDashboard";
import { api } from "./lib/api";
import { readableError } from "./lib/errors";
import { useSessionSocket } from "./lib/useSessionSocket";
import type { BootstrapResponse, User } from "./types";

type AuthView = "login" | "setup" | "recover";

function readSetupToken() {
  const parameters = new URLSearchParams(window.location.hash.slice(1));
  const token = parameters.get("setup") ?? "";
  if (token) {
    window.sessionStorage.setItem("velvet_setup_token", token);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return token;
  }
  return window.sessionStorage.getItem("velvet_setup_token") ?? "";
}

export default function App() {
  const [setupToken] = useState(readSetupToken);
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authView, setAuthView] = useState<AuthView>(
    setupToken ? "setup" : "login"
  );
  const [page, setPage] = useState<PageName>("session");
  const [fatalError, setFatalError] = useState("");
  const socket = useSessionSocket(user);

  async function refreshBootstrap() {
    try {
      const next = await api.bootstrap();
      setBootstrap(next);
      setUser(next.user);
      return next;
    } catch (cause) {
      setFatalError(readableError(cause));
      return null;
    }
  }

  useEffect(() => {
    void refreshBootstrap();
  }, []);

  useEffect(() => {
    if (!socket.lastError) return;
    const timer = window.setTimeout(socket.clearError, 5_000);
    return () => window.clearTimeout(timer);
  }, [socket.lastError]);

  async function login(email: string, password: string, remember: boolean) {
    const result = await api.login(email, password, remember);
    setUser(result.user);
    setPage("session");
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setPage("session");
  }

  if (!bootstrap) {
    return (
      <main className="loading-screen">
        <div className="brand__mark"><i /><i /></div>
        <p>{fatalError || "Opening your private space…"}</p>
      </main>
    );
  }

  if (!user) {
    if (authView === "setup") {
      return (
        <SetupScreen
          setupToken={setupToken}
          onCancel={() => setAuthView("login")}
          onComplete={async () => {
            await refreshBootstrap();
            setAuthView("login");
          }}
        />
      );
    }
    if (authView === "recover") {
      return <RecoveryScreen onDone={() => setAuthView("login")} />;
    }
    return (
      <LoginScreen
        setupRequired={bootstrap.setupRequired}
        setupAvailable={!bootstrap.setupProtected || Boolean(setupToken)}
        onLogin={login}
        onSetup={() => setAuthView("setup")}
        onRecover={() => setAuthView("recover")}
      />
    );
  }

  return (
    <AppShell
      user={user}
      page={page}
      setPage={setPage}
      onLogout={() => void logout()}
    >
      {!socket.state ? (
        <main className="loading-screen loading-screen--inside">
          <div className="brand__mark"><i /><i /></div>
          <p>Connecting to the private relay…</p>
        </main>
      ) : page === "session" ? (
        user.role === "wearer" ? (
          <WearerDashboard
            user={user}
            state={socket.state}
            relayConnected={socket.status === "connected"}
            lastIntent={socket.lastIntent}
            send={socket.send}
          />
        ) : (
          <ControllerDashboard
            user={user}
            state={socket.state}
            relayConnected={socket.status === "connected"}
            send={socket.send}
          />
        )
      ) : page === "devices" ? (
        <DevicesPage user={user} state={socket.state} />
      ) : page === "history" ? (
        <HistoryPage state={socket.state} />
      ) : (
        <SafetyPage />
      )}
      {socket.lastError && (
        <div className="toast" role="status">
          {readableError(new Error(socket.lastError))}
        </div>
      )}
    </AppShell>
  );
}
